import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import infImg from '../../assets/images/infIMg.png';
import filterImg from '../../assets/images/Vector.png';
import drone from '../../assets/images/eos-icons_drone.png';
import battery from '../../assets/images/battery.png';
import grayImg from '../../assets/images/grayImg.png';
import ABCImg from '../../assets/images/Abcimg.png';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/reducers';
import YardCanvas, { YardSetup } from '../yard/YardCanvas';
import type { SelectedTrailer } from '../yard/YardCanvas';
import Trailer3DViewer from '../yard/Trailer3DViewer';
import { getYardLayout } from '../../services/yard.service';
import type { FlightRoute } from '../yard/FlightPathOverlay';
import {
    MISSION_PREVIEW_EVENT,
    getLatestMissionPreview,
    CompiledMission
} from '../../services/mission-preview';
import { gpsToYardPixel } from '../../utils/missionCoordinates';



export default function MainDashboard(): React.ReactElement {
    const { user } = useSelector((e: RootState) => ({ user: e.user }))


    const [setupData, setSetupData] = useState<YardSetup | null>(null);
    const [selectedTrailer, setSelectedTrailer] =
        useState<SelectedTrailer | null>(null);
    const [compiledMission, setCompiledMission] =
        useState<CompiledMission | null>(() => getLatestMissionPreview());

    const [previewError, setPreviewError] =
        useState<string | null>(null);

    useEffect(() => {
        const handlePreview = (event: Event) => {
            const mission = (event as CustomEvent<CompiledMission>).detail;

            if (
                mission?.success !== true ||
                mission.flight_executed !== false ||
                !mission.mavsdk?.sequence ||
                !mission.metrics
            ) {
                setCompiledMission(null);
                setPreviewError("Mission validation was unsuccessful.");
                return;
            }

            setCompiledMission(mission);
            setPreviewError(null);
        };

        window.addEventListener(MISSION_PREVIEW_EVENT, handlePreview);

        return () => {
            window.removeEventListener(MISSION_PREVIEW_EVENT, handlePreview);
        };
    }, []);

    const routeResult: {
        route: FlightRoute | null;
        error: string | null;
    } = (() => {
        if (!compiledMission?.mavsdk?.sequence) {
            return { route: null, error: null };
        }

        if (!setupData) {
            return {
                route: null,
                error: "Yard layout is unavailable. Flight route cannot be displayed."
            };
        }

        try {
            if (!setupData.georef) {
                throw new Error("Yard GPS calibration is unavailable.");
            }

            const home = setupData.pod?.home_px;
            const waypoints: FlightRoute["waypoints"] = [];

            const sequence = compiledMission.mavsdk.sequence;

            for (let stepIndex = 0; stepIndex < sequence.length; stepIndex++) {
                const step = sequence[stepIndex];
                const command = step.command.toLowerCase();

                if (command === "takeoff") {
                    if (!home) {
                        throw new Error("POD home position is unavailable.");
                    }

                    waypoints.push({
                        position: home,
                        label: "POD Takeoff",
                        action: "takeoff",
                        altitudeFt: step.altitude_m !== undefined
                            ? step.altitude_m * 3.28084
                            : undefined
                    });
                } else if (command === "goto") {
                    if (
                        !Number.isFinite(step.lat) ||
                        !Number.isFinite(step.lon)
                    ) {
                        throw new Error("Mission waypoint GPS coordinates are invalid.");
                    }

                    const position = gpsToYardPixel(
                        step.lat as number,
                        step.lon as number,
                        setupData.georef
                    );

                    const isFinalGoto = !sequence.slice(stepIndex + 1)
                        .some(next => next.command.toLowerCase() === "goto");

                    const isPodReturn = isFinalGoto &&
                        sequence.slice(stepIndex + 1)
                            .some(next => next.command.toLowerCase() === "land") &&
                        Array.isArray(home) &&
                        Math.hypot(position[0] - home[0], position[1] - home[1]) <= 5;

                    waypoints.push({
                        position,
                        label: isPodReturn
                            ? "Return to POD"
                            : `Waypoint ${waypoints.filter(w => w.action === "goto").length + 1}`,
                        action: isPodReturn ? "return" : "goto",
                        altitudeFt: step.altitude_m !== undefined
                            ? step.altitude_m * 3.28084
                            : undefined
                    });
                } else if (command === "rtl") {
                    // RTL is a flight-controller command, not a verified GPS route.
                    // Keep the known outbound waypoints without inventing a return path.
                    continue;
                } else if (command === "land") {
                    if (home) {
                        const previous = waypoints[waypoints.length - 1];
                        if (
                            previous &&
                            Math.hypot(
                                previous.position[0] - home[0],
                                previous.position[1] - home[1]
                            ) <= 5
                        ) {
                            waypoints.push({
                                position: home,
                                label: "Land at POD",
                                action: "land"
                            });
                        }
                    }
                }
            }

            if (waypoints.length === 0) {
                throw new Error("Compiled mission contains no displayable flight waypoints.");
            }

            return { route: { waypoints }, error: null };
        } catch (error) {
            console.error("Unable to map compiled mission:", error);

            return {
                route: null,
                error: error instanceof Error
                    ? error.message
                    : "Unable to display the compiled flight route."
            };
        }
    })();

    const flightRoute: FlightRoute | null = routeResult.route;
    const flightRouteError = previewError ?? routeResult.error;

    useEffect(() => {
        let active = true;

        async function loadLayout() {
            try {
                const response = await getYardLayout();

                if (active) {
                    setSetupData(response.layout);
                }
            } catch (error: any) {
                if (active) {
                    setSetupData(null);

                    if (error.response?.status !== 404) {
                        console.error("Unable to load yard layout:", error);
                    }
                }
            }
        }

        loadLayout();

        return () => {
            active = false;
        };
    }, []);

    const vehicleTypes = [
        {
            label: "Dry Van (2)",
            borderColor: "#D9D9D9",
            textColor: "#D9D9D9",
            img: grayImg,
        },
        {
            label: "Reefer (13)",
            borderColor: "#D9D9D9",
            textColor: "#D9D9D9",
            img: grayImg,
        },
        {
            label: "Intermodal (20)",
            borderColor: "#D9D9D9",
            textColor: "#D9D9D9",
            img: grayImg,
        },
        {
            label: "Willow (18)",
            borderColor: "#D9D9D9",
            textColor: "#D9D9D9",
            img: grayImg,
        },
    ];

    return (
        <>
        <div className="bg-gray-4  rounded overflow-hidden flex flex-col p-4 h-full">
            <div className='flex justify-between items-center'>
                {/* Dropdown */}
                <div className="w-full max-w-[140px] mb-4 relative flex items-center justify-center">
                    <select
                        disabled
                        className="w-full appearance-none p-2.5 border border-gray-2 bg-white rounded text-gray-2 text-base md:text-lg pr-8 cursor-not-allowed "
                    >
                        <option>Full View</option>
                        <option>Zone A</option>
                        <option>Zone B</option>
                        <option>Custom View</option>
                    </select>
                    {/* Custom arrow */}
                    <div className="pointer-events-none absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-2 text-xl">
                        <Icon
                            icon="material-symbols:keyboard-arrow-down"
                            className="icon-color"
                            width={40}
                            height={20}
                        />
                    </div>
                </div>
                <div className='flex flex-row  2xl:flex-col gap-3'>
                    <img src={filterImg} alt="" className='size-[25px] 2xl:size-[35px]' />
                    <img src={infImg} alt="" className='size-[25px] 2xl:size-[35px]' />
                </div>
            </div>


            {compiledMission?.metrics && (
                <div className="mb-3 rounded-lg border border-gray-300 bg-white p-4 text-sm text-gray-800">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-base font-bold">
                            Validated Mission Preview
                        </h3>
                        <span className="font-semibold text-blue-700">
                            Planning Only — Drone Not Dispatched
                        </span>
                    </div>

                    <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
                        <div>
                            <div className="text-gray-500">Estimated Flight Time</div>
                            <div className="font-bold">
                                {compiledMission.metrics.total_time_min.toFixed(1)} min
                            </div>
                        </div>
                        <div>
                            <div className="text-gray-500">Flight Distance</div>
                            <div className="font-bold">
                                {compiledMission.metrics.distance_mi.toFixed(2)} mi
                            </div>
                        </div>
                        <div>
                            <div className="text-gray-500">Waypoints</div>
                            <div className="font-bold">
                                {compiledMission.metrics.waypoints_visited}
                            </div>
                        </div>
                        <div>
                            <div className="text-gray-500">Flight Altitude</div>
                            <div className="font-bold">
                                {(() => {
                                    const altitudes = compiledMission.mavsdk?.sequence
                                        .filter(step => Number.isFinite(step.altitude_m))
                                        .map(step => Math.round((step.altitude_m as number) * 3.28084)) ?? [];
                                    return altitudes.length
                                        ? `${Math.min(...altitudes)}–${Math.max(...altitudes)} ft`
                                        : "Not specified";
                                })()}
                            </div>
                        </div>
                    </div>

                    <details>
                        <summary className="cursor-pointer font-semibold">
                            Mission Steps
                        </summary>
                        <ol className="mt-2 max-h-40 list-decimal overflow-y-auto pl-5">
                            {compiledMission.mavsdk?.sequence.map((step, index) => (
                                <li key={index} className="mb-1">
                                    {step.command.toUpperCase()}
                                    {Number.isFinite(step.altitude_m) &&
                                        ` — ${((step.altitude_m as number) * 3.28084).toFixed(0)} ft`}
                                    {Number.isFinite(step.duration_s) &&
                                        ` — ${step.duration_s} sec`}
                                    {Number.isFinite(step.lat) &&
                                     Number.isFinite(step.lon) &&
                                        ` — GPS ${(step.lat as number).toFixed(6)}, ${(step.lon as number).toFixed(6)}`}
                                </li>
                            ))}
                        </ol>
                    </details>
                </div>
            )}

            {flightRouteError && (
                <div
                    role="alert"
                    className="mb-3 rounded border border-red-400 bg-red-50 p-3 text-sm text-red-800"
                >
                    <strong>Flight Preview Error: </strong>
                    {flightRouteError}
                </div>
            )}

            {/* Image Container */}
            <div className="mt-auto flex flex-col justify-center items-center h-full">
                <div className='relative w-full'>
                    <div className="w-full h-[500px] xl:h-[600px] 2xl:h-[700px]">
                        {setupData ? (
                            <YardCanvas
                                setupData={setupData}
                                flightRoute={flightRoute}
                                onTrailerClick={setSelectedTrailer}
                            />
                        ) : (
                            <img
                                src={ABCImg}
                                alt="Default yard layout"
                                className="w-full h-full object-contain"
                            />
                        )}
                    </div>
                </div>

            {selectedTrailer && createPortal(
                <div
                    role="presentation"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) {
                            setSelectedTrailer(null);
                        }
                    }}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 9999,
                        background: 'rgba(0,0,0,0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 20
                    }}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label="3D Trailer Viewer"
                        style={{
                            background: '#ffffff',
                            borderRadius: 12,
                            width: 'min(1100px, 96vw)',
                            maxHeight: '94vh',
                            overflow: 'auto',
                            padding: 20
                        }}
                    >
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h2 className="text-xl font-bold">
                                    Trailer {selectedTrailer.number}
                                </h2>
                                <p className="text-sm text-gray-600">
                                    {selectedTrailer.type} — {selectedTrailer.length} ft
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setSelectedTrailer(null)}
                                aria-label="Close trailer viewer"
                                className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
                            >
                                Close
                            </button>
                        </div>

                        {selectedTrailer.length === 53 &&
                         /dry[\s_-]*van/i.test(selectedTrailer.type) ? (
                            <div style={{ height: 'min(65vh, 650px)' }}>
                                <Trailer3DViewer
                                    modelType={
                                        selectedTrailer.skirts === 'no'
                                            ? '53ft-dry-van-no-skirts'
                                            : '53ft-dry-van'
                                    }
                                />
                            </div>
                        ) : (
                            <div className="p-8 text-center text-gray-600">
                                A 3D model is not yet available for this trailer type.
                            </div>
                        )}

                        <p className="text-xs text-gray-500 mt-3">
                            Left drag: rotate · Scroll: zoom · Right drag: pan
                        </p>
                    </div>
                </div>,
                document.body
            )}

                {/* Filters - Always visible */}
                <div className="flex  items-center gap-2 justify-center w-full">
                    <span className='text-gray-2 text-sm 2xl:text-lg font-semibold font-[Geist-semibold]'>Yard Filter</span>
                    <div className='bg-gray-1 p-2  rounded'>
                        <div className="flex p-1 space-x-2 xl:space-x-4 items-center">
                            {vehicleTypes.map((type, index) => (
                                <div
                                    key={index}
                                    className="flex gap-2 px-4 py-2 border rounded text-sm bg-white items-center"
                                    style={{ borderColor: type.borderColor, color: type.textColor }}
                                >
                                    <img src={type.img} alt="" className="size-[13px]" />
                                    <span className='text-xs 2xl:text-sm'>{type.label}</span>
                                </div>
                            ))}

                        </div>
                    </div>

                    <span className='text-gray-2 text-sm 2xl:text-lg font-semibold font-[Geist-semibold]'>Drones</span>
                    <div className='bg-gray-1 p-2  rounded'>
                        <div className="flex gap-2 border border-gray-4 px-4 py-1 rounded text-sm bg-white items-center">
                            <img src={drone} alt="" className='size-[13px]' />
                            <span className='pr-2 border-r-2 border-gray-3 text-gray-2 text-sm 2xl:text-lg'>15</span>
                            <img src={battery} alt="" className='size-[13px]' />

                        </div>
                    </div>

                </div>
            </div>

        </div>
    </>
    );
};